import { useEffect, useRef, useState } from "react";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Spinner from "react-bootstrap/Spinner";
import { submitSalesInquiry } from "../api.js";

export default function SalesForm({ user, token, initialMessage = "" }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const messageRef = useRef(null);

  // Auto-grow the message textarea to fit its content (e.g. the multi-line
  // pre-filled quote) so nothing is hidden behind a scrollbar. Reset to "auto"
  // first so it shrinks back when text is removed; min-height keeps the empty
  // baseline at ~4 rows.
  useEffect(() => {
    const el = messageRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [form.message]);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, name: user.name ?? "", email: user.email ?? "" }));
  }, [user]);

  // Seed a handed-off quote message (from "Request for Quote") when it arrives.
  // Keyed on the incoming value, not mount, so the prefill applies once the
  // parent supplies it; the parent consumes the router state once, so this won't
  // clobber the user's later edits.
  useEffect(() => {
    if (initialMessage) setForm((f) => ({ ...f, message: initialMessage }));
  }, [initialMessage]);

  function set(field) {
    return (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await submitSalesInquiry({ name: form.name, email: form.email, phone: form.phone, message: form.message }, token);
      setSubmitted(true);
    } catch (err) {
      setError(err.message || "Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <Alert variant="success" className="mb-0">
        Thank you for reaching out! We will get back to you shortly.
      </Alert>
    );
  }

  return (
    <Form onSubmit={handleSubmit}>
      <h5 className="pmp-contact-form-title">Sales Inquiry</h5>
      <p className="text-secondary small mb-4">Fill out the form and a member of our account team will follow up with you shortly.</p>
      {error && <Alert variant="danger">{error}</Alert>}
      <Row className="g-3 mb-3">
        <Col sm={6}>
          <Form.Group>
            <Form.Label>Name <span className="text-danger">*</span></Form.Label>
            <Form.Control required value={form.name} onChange={set("name")} placeholder="Your name" />
          </Form.Group>
        </Col>
        <Col sm={6}>
          <Form.Group>
            <Form.Label>Email <span className="text-danger">*</span></Form.Label>
            <Form.Control required type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" />
          </Form.Group>
        </Col>
      </Row>
      <Form.Group className="mb-3">
        <Form.Label>Phone <span className="text-secondary small fw-normal">(optional)</span></Form.Label>
        <Form.Control type="tel" value={form.phone} onChange={set("phone")} placeholder="(000) 000-0000" />
      </Form.Group>
      <Form.Group className="my-4">
        <Form.Label>Message</Form.Label>
        <Form.Control ref={messageRef} as="textarea" rows={4} value={form.message} onChange={set("message")} placeholder="Tell us about your supply needs, volumes, or any questions…" style={{ minHeight: "7rem", overflow: "hidden", resize: "none", lineHeight: 1.7, padding: "1rem 1.1rem" }} />
        <Form.Text className="d-block mt-2 fst-italic text-secondary" style={{ fontSize: "0.8rem", lineHeight: 1.5 }}>
          Thank you for reaching out — we'll get back to you as soon as we can.
          Product availability and pricing are subject to change.
        </Form.Text>
      </Form.Group>
      <div className="d-flex justify-content-end">
        <Button type="submit" variant="primary" disabled={submitting}>
          {submitting ? <><Spinner size="sm" className="me-2" />Sending…</> : "Send Inquiry"}
        </Button>
      </div>
    </Form>
  );
}
